<template>
  <div class="br-intro">
    <div class="page-header">
      <router-link to="/br-guides" class="back-link">← Back</router-link>
      <h1>BR Introduction</h1>
      <p class="page-desc">Everything you need to know about Cloudflare Browser Rendering.</p>
    </div>
    <p class="text-muted"></p>

    <div id="accordionExample" aria-multiselectable="false">
      <b-card no-body class="mb-2">
        <b-card-header
          header-tag="header"
          class="p-0"
          role="tab"
          id="headingOne"
        >
          <h2 class="mb-0">
            <b-button
              v-b-toggle.collapseOne
              variant="link"
              block
              class="text-left"
            >
              什么是Browser Rendering？
            </b-button>
          </h2>
        </b-card-header>

        <b-collapse
          id="collapseOne"
          accordion="accordionExample"
          class="border-top"
          role="tabpanel"
          aria-labelledby="headingOne"
          block
        >
          <b-card-body>
            Browser Rendering 使开发人员能够以编程方式控制和与 Cloudflare
            全球网络上运行的无头(headless)浏览器实例进行交互，实现自动化浏览器交互、截取屏幕截图、生成
            PDF 以及从网页提取数据等任务。
          </b-card-body>
        </b-collapse>
      </b-card>

      <!-- 比较表格卡 -->
      <b-card no-body class="mb-2">
        <b-card-header
          header-tag="header"
          class="p-0"
          role="tab"
          id="headingTwo"
        >
          <h2 class="mb-0">
            <b-button
              v-b-toggle.collapseTwo
              variant="link"
              block
              class="text-left"
            >
              Free Plan & Paid Plan 使用 BR 有什么区别？
            </b-button>
          </h2>
        </b-card-header>

        <b-collapse
          id="collapseTwo"
          accordion="accordionExample"
          class="border-top"
          role="tabpanel"
          aria-labelledby="headingTwo"
        >
          <b-card-body>
            Workers Free 和 Workers Paid plan 的客户都可以使用 Browser
            Rendering。
            <div class="compare-table mt-3">
              <!-- 表头 -->
              <div class="table-row header-row">
                <div class="col col-feature">Feature</div>
                <div class="col col-free text-center">Free Plan</div>
                <div class="col col-paid text-center">Paid Plan</div>
              </div>

              <!-- 数据行（5 行） -->
              <div
                v-for="(row, idx) in rows"
                :key="idx"
                class="table-row"
                :class="{ 'last-row': idx === rows.length - 1 }"
              >
                <div class="col col-feature">
                  <div class="feature-text">{{ row.feature }}</div>
                </div>
                <div class="col col-free text-center">
                  <div class="cell-text">{{ row.free }}</div>
                </div>
                <div class="col col-paid text-center">
                  <div class="cell-text">{{ row.paid }}</div>
                </div>
              </div>
            </div>
            <!-- /compare-table -->
          </b-card-body>
        </b-collapse>
      </b-card>

      <!-- 第三个卡片：在此处插入图片 -->
      <b-card no-body class="mb-2">
        <b-card-header
          header-tag="header"
          class="p-0"
          role="tab"
          id="headingThree"
        >
          <h2 class="mb-0">
            <b-button
              v-b-toggle.collapseThree
              variant="link"
              block
              class="text-left"
            >
              BR 可以结合哪些产品使用？
            </b-button>
          </h2>
        </b-card-header>

        <b-collapse
          id="collapseThree"
          accordion="accordionExample"
          class="border-top"
          role="tabpanel"
          aria-labelledby="headingThree"
        >
          <b-card-body>
            Browser Rendering 可以结合 Cloudflare 的 Workers、Durable
            Objects、Agents (MCP)、workers AI、R2、KV storage 等。

            <!-- 在这里插入图片：图片使用 data 中的 imageUrl -->
            <div class="diagram-wrapper mt-3">
              <img
                :src="require('@/assets/br.drawio.png')"
                :alt="imageAlt"
                class="img-fluid diagram-img"
              />
            </div>
          </b-card-body>
        </b-collapse>
      </b-card>

      <!-- 第四个卡片（已经修复为独立 id） -->
      <b-card no-body class="mb-2">
        <b-card-header
          header-tag="header"
          class="p-0"
          role="tab"
          id="headingFour"
        >
          <h2 class="mb-0">
            <b-button
              v-b-toggle.collapseFour
              variant="link"
              block
              class="text-left"
            >
              BR 的2种使用方式？
            </b-button>
          </h2>
        </b-card-header>

        <b-collapse
          id="collapseFour"
          accordion="accordionExample"
          class="border-top"
          role="tabpanel"
          aria-labelledby="headingFour"
        >
          <b-card-body>
            <p>
              <strong style="color: skyblue">REST API</strong
              >：最适合执行简单的无状态任务，例如截取屏幕截图、生成 PDF、提取
              HTML 内容等；
            </p>
            <p>
              <strong style="color: skyblue">Workers Bindings</strong>：最适合在
              Cloudflare Workers 中实现高级浏览器自动化；
            </p>
          </b-card-body>
        </b-collapse>
      </b-card>

      <!-- 新增卡片 5 -->
      <b-card no-body class="mb-2">
        <b-card-header
          header-tag="header"
          class="p-0"
          role="tab"
          id="headingFive"
        >
          <h2 class="mb-0">
            <b-button
              v-b-toggle.collapseFive
              variant="link"
              block
              class="text-left"
            >
              BR 的优势
            </b-button>
          </h2>
        </b-card-header>

        <b-collapse
          id="collapseFive"
          accordion="accordionExample"
          class="border-top"
          role="tabpanel"
          aria-labelledby="headingFive"
        >
          <b-card-body>
            <p>1. <strong style="color: skyblue">可扩展至数千个浏览器</strong>：即时访问全球浏览器池，冷启动时间短，非常适合大规模屏幕截图生成、数据提取或自动化操作。</p>
            <p>2. <strong style="color: skyblue">默认全球部署</strong>：浏览器会话运行在 Cloudflare 的边缘网络上，靠近终端用户，从而提升速度和全球可用性。</p>
            <p>3. <strong style="color: skyblue">易于集成</strong>：提供用于常用操作的 REST API，同时还提供 <strong>Puppeteer</strong> 和 <strong>Playwright </strong>等常用自动化library，方便处理复杂的工作流程。</p>
            <p>4. <strong style="color: skyblue">会话管理</strong>：在请求之间复用浏览器会话，提升性能并降低冷启动消耗。</p>
          </b-card-body>
        </b-collapse>
      </b-card>

      <!-- 新增卡片 6 -->
      <!-- 卡片 6：将用户提供的 list-group 整合到此处 -->
      <b-card no-body class="mb-2">
        <b-card-header
          header-tag="header"
          class="p-0"
          role="tab"
          id="headingSix"
        >
          <h2 class="mb-0">
            <b-button
              v-b-toggle.collapseSix
              variant="link"
              block
              class="text-left"
            >
              常用case
            </b-button>
          </h2>
        </b-card-header>

        <b-collapse
          id="collapseSix"
          accordion="accordionExample"
          class="border-top"
          role="tabpanel"
          aria-labelledby="headingSix"
        >
          <b-card-body>
            <!-- 整合的 list-group（与用户原始代码保持一致） -->
            <div class="list-group">
              <a href="#" class="list-group-item list-group-item-action ">
                <div class="d-flex w-100 justify-content-between">
                  <h5 class="mb-1" style="color: skyblue">1.自动从网站提取结构化数据</h5>
                </div>
                <p class="mb-1">
                  我的使用场景是需要从电商店铺页面提取结构化数据（JSON格式），例如标题、描述、图片URL、价格等。<br>
                  以前，手动从eBay抓取价格需要3-4天时间。现在我们使用 BR 实现了自动化。<br>
                  我们在企业入驻过程中进行数据丰富，需要抓取大量数据。<br>
                </p>
              </a>
              <a href="#" class="list-group-item list-group-item-action">
                <div class="d-flex w-100 justify-content-between">
                  <h5 class="mb-1" style="color: skyblue">2.大规模从网页和 HTML 内容生成 PDF</h5>
                  <small class="text-muted"></small>
                </div>
                <p class="mb-1">我们用它来让用户生成并下载PDF格式的许可证和收据。<br>
                我们使用Puppeteer生成各种格式的网络研讨会证书。<br>
                </p>
            <div class="diagram-wrapper mt-3">
              <img
                :src="require('@/assets/contentgen.drawio.png')"
                :alt="imageAlt"
                class="img-fluid diagram-img"
              />
            </div>
                <small class="text-muted"></small>
              </a>
              <a href="#" class="list-group-item list-group-item-action">
                <div class="d-flex w-100 justify-content-between">
                  <h5 class="mb-1" style="color: skyblue">3.创建网站缩略图和社交预览</h5>
                </div>
                <p class="mb-1">我们正在使用 API 为社区创建的网站生成截图预览。<br>
                我们的仪表板展示了客户创建的网页内容……我们需要为他们所做的更改生成缩略图。<br>
                为共享内容提供预览图像的支持。<br>
                </p>
                <small class="text-muted"></small>
              </a>
              <a href="#" class="list-group-item list-group-item-action">
                <div class="d-flex w-100 justify-content-between">
                  <h5 class="mb-1" style="color: skyblue">4.监控和扫描 Web 内容</h5>
                </div>
                <p class="mb-1">CF Radar 的  <a href="https://radar.cloudflare.com/scan" target="_blank" rel="noopener noreferrer">URL 扫描器</a>使用 BR 来加载和呈现网页，以捕获 DOM 快照、网络日志（HAR）及不同设备尺寸下的屏幕截图，用于网站分析。<br>
                CF 为每期新发布的时事通讯截取一张屏幕截图/生成一份 PDF，并将其保存在 R2 中。<br>
                为 AI 分析客户线索进行批量截图。<br>
                CF 构建了这个很酷的技术栈查找工具，用于扫描网站。<br>
                </p>
                <small class="text-muted"></small>
              </a>
              <a href="#" class="list-group-item list-group-item-action">
                <div class="d-flex w-100 justify-content-between">
                  <h5 class="mb-1" style="color: skyblue">5.在 Web 上赋能 AI Agent </h5>
                </div>
                <p class="mb-1">我们正在构建一个模型，以帮助用户完成任务——比如办理航班登机手续。<br>
                我们使用 BR 从静态网站获取数据用于检索增强型生成（RAG）。<br>
                为 AI 分析客户线索进行批量截图。<br>
                我们会对网站和其他由我们的 AI 代理生成的资产进行截图。<br>
                </p>
                <small class="text-muted"></small>
              </a>
            </div>
          </b-card-body>
        </b-collapse>
      </b-card>

      <!-- 新增卡片 7 -->
      <b-card no-body class="mb-2">
        <b-card-header
          header-tag="header"
          class="p-0"
          role="tab"
          id="headingSeven"
        >
          <h2 class="mb-0">
            <b-button
              v-b-toggle.collapseSeven
              variant="link"
              block
              class="text-left"
            >
              BR 的计费与定价说明
            </b-button>
          </h2>
        </b-card-header>

        <b-collapse
          id="collapseSeven"
          accordion="accordionExample"
          class="border-top"
          role="tabpanel"
          aria-labelledby="headingSeven"
        >
          <b-card-body>
            <p>1. <strong style="color:skyblue">REST API</strong>: Charged for Duration only ($/browser hour)</p>
            <p>2. <strong style="color:skyblue">Workers Bindings</strong>: Charged for both Duration and Concurrency ($/browser hour + # of concurrent browsers)</p>
            
          </b-card-body>
        </b-collapse>
      </b-card>

      <!-- 第八个卡片：把 BR vs Pages 对比表放在这里 -->
      <b-card no-body class="mb-2">
        <b-card-header
          header-tag="header"
          class="p-0"
          role="tab"
          id="headingEight"
        >
          <h2 class="mb-0">
            <b-button
              v-b-toggle.collapseEight
              variant="link"
              block
              class="text-left"
            >
              BR vs Pages
            </b-button>
          </h2>
        </b-card-header>

        <b-collapse
          id="collapseEight"
          accordion="accordionExample"
          class="border-top"
          role="tabpanel"
          aria-labelledby="headingEight"
        >
          <b-card-body>
            <!-- 插入 BR vs Pages 对比表 -->
            <div class="compare-table mt-1">
              <!-- 表头 -->
              <div class="table-row header-row">
                <div class="col col-feature">维度</div>
                <div class="col col-pages text-center">Cloudflare Pages（静态 / SSR）</div>
                <div class="col col-br text-center">Browser Rendering（DO 动态 "Pages"）</div>
              </div>

              <!-- 数据行 -->
              <div
                v-for="(row, idx) in comparisonRows"
                :key="idx"
                class="table-row"
                :class="{ 'last-row': idx === comparisonRows.length - 1 }"
              >
                <div class="col col-feature">
                  <div class="feature-text">{{ row.dimension }}</div>
                </div>
                <div class="col col-pages text-left">
                  <div class="cell-text" v-html="row.pages"></div>
                </div>
                <div class="col col-br text-left">
                  <div class="cell-text" v-html="row.browser"></div>
                </div>
              </div>
            </div>

            <div class="hint mt-3">
              建议: 先使用 Pages/SSR 输出基础 HTML；仅在用户/任务需要时再触发 Browser Rendering（可节省成本）。
            </div>
          </b-card-body>
        </b-collapse>
      </b-card>
    </div>
  </div>
</template>

<script>
export default {
  name: "BrIntroduction",
  data() {
    return {
      // 五行三列的数据源（可按需修改）
      rows: [
        {
          feature: "Concurrent browsers per account (Workers Bindings only)",
          free: "3 per account",
          paid: "30 per account",
        },
        {
          feature: "New browser instances per minute (Workers Bindings only)",
          free: "3 per minute",
          paid: "30 per minute",
        },
        {
          feature: "Browser timeout",
          free: "60 seconds",
          paid: "60 seconds",
        },
        {
          feature: "Total requests per min (REST API only)",
          free: "6 per minute",
          paid: "180 per minute",
        },
      ],

      // 新增：BR vs Pages 的对比数据（放在第八个卡片）
      comparisonRows: [
        {
          dimension: "目标场景",
          pages:
            "静态站点、或后端 SSR 已输出完整 HTML（例如博客、文档、营销页）",
          browser:
            "SPA 或依赖大量客户端 JS 的页面、需要运行 JS 才能得到最终 DOM 或截图（例如仪表盘、动态渲染的电商页）",
        },
        {
          dimension: "渲染模型",
          pages:
            "直接返回静态 HTML 或客户端渲染无需在服务器端执行页面 JS",
          browser:
            "在 headless 浏览器中执行页面 JS（Puppeteer / Playwright），获取渲染后的 DOM 与截图",
        },
        {
          dimension: "运行环境",
          pages:
            "Cloudflare Pages / Workers（边缘） — 无需维持长期进程",
          browser:
            "Durable Object (作为浏览器管理器) + PuppeteerWorkers（或 CF 的 puppeteer 集成）",
        },
        {
          dimension: "有无状态",
          pages:
            "无状态或短期 request-scoped（可用 Workers KV / R2 做持久化）",
          browser:
            "有状态（DO 可持久化 task queue、缓存、管理浏览器生命周期）",
        },
        {
          dimension: "延迟与启动时间",
          pages: "响应快，CDN 缓存命中时几乎无延迟",
          browser:
            "相对较高：包含浏览器启动、newPage、等待网络空闲等步骤（秒级）",
        },
        {
          dimension: "资源消耗",
          pages: "低：仅负责传输与少量边缘计算",
          browser: "高：浏览器占用内存/CPU，尤其是并发时",
        },
        {
          dimension: "可扩展性",
          pages:
            "自动水平扩展，适合高并发请求（由 Pages/Workers/CDN 处理）",
          browser:
            "需限流与队列（DO 可管理并发），水平扩展更复杂且需控制成本",
        },
        {
          dimension: "缓存策略",
          pages:
            "构建时或边缘可长期缓存（TTL），非常节省成本和带宽",
          browser:
            "把渲染结果缓存到 R2 或 Workers Cache，并设置合理 TTL，避免重复渲染",
        },
        {
          dimension: "安全性",
          pages: "常规边缘安全（WAF、CDN）即可",
          browser:
            "需限制内网访问、在 page 上拦截并阻止不必要的第三方资源",
        },
        {
          dimension: "推荐使用场景",
          pages:
            "博客、文档、静态产品页、SEO 友好的页面优先使用 Pages/SSR",
          browser:
            "需要执行页面 JS 才能获取内容、截图、PDF、复杂自动化流程时使用 Browser Rendering",
        },
      ],
    };
  },
};
</script>

<style scoped>
.br-intro {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  text-align: left;
}

/* Page header */
.page-header { margin-bottom: 1.5rem; }
.back-link {
  display: inline-block;
  font-size: 13px;
  font-weight: 500;
  color: #6B7280;
  text-decoration: none;
  margin-bottom: 0.75rem;
  transition: color 0.15s;
}
.back-link:hover { color: #F6821F; }
h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #F6821F;
  letter-spacing: -0.03em;
  margin: 0 0 0.4rem;
}
.page-desc { font-size: 14px; color: #6B7280; margin: 0; line-height: 1.5; }

/* Accordion wrapper */
.br-intro #accordionExample {
  width: 100%;
  margin-top: 0.5rem;
}

/* Override Bootstrap-Vue card styles */
.br-intro ::v-deep .card {
  border: 1px solid #E4E4E0!important;
  border-radius: 10px!important;
  overflow: hidden;
  margin-bottom: 0.6rem!important;
  box-shadow: none!important;
}
.br-intro ::v-deep .card-header {
  background: #FFFFFF!important;
  border-bottom: none!important;
  padding: 0!important;
}
.br-intro ::v-deep .card-header .btn-link {
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1D1D1D!important;
  text-decoration: none;
  padding: 0.9rem 1.1rem;
  width: 100%;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.br-intro ::v-deep .card-header .btn-link:hover { color: #F6821F!important; }
.br-intro ::v-deep .collapse .card-body,
.br-intro ::v-deep .collapsing .card-body {
  background: #FAFAFA!important;
  border-top: 1px solid #E4E4E0!important;
  color: #374151!important;
  font-size: 14px!important;
  line-height: 1.65!important;
  padding: 1rem 1.1rem!important;
}

/* Compare table */
.compare-table {
  width: 100%;
  overflow-x: auto;
  margin-top: 0.75rem;
  border: 1px solid #E4E4E0;
  border-radius: 8px;
  overflow: hidden;
}
.table-row {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #E4E4E0;
  background: #FFFFFF;
}
.table-row:last-child { border-bottom: none; }
.table-row.last-row { border-bottom: none; }
.header-row {
  background: #F5F4F2;
  font-weight: 600;
  font-size: 13px;
  color: #374151;
}
.col {
  padding: 10px 14px;
  box-sizing: border-box;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
}
.col-feature { flex: 2 1 42%; text-align: left; border-right: 1px solid #E4E4E0; }
.col-free, .col-paid, .col-pages, .col-br { flex: 1 1 27%; }
.feature-text { font-weight: 500; color: #1D1D1D; }
.cell-text { color: #374151; }

/* Diagram */
.diagram-wrapper { display: flex; justify-content: center; margin-top: 0.75rem; }
.diagram-img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 18px rgba(0,0,0,0.08);
  border: 1px solid #E4E4E0;
}

/* List group items */
.br-intro .list-group { display: flex; flex-direction: column; gap: 0.5rem; }
.br-intro .list-group .list-group-item {
  background: #FFFFFF!important;
  border: 1px solid #E4E4E0!important;
  border-radius: 8px!important;
  padding: 0.85rem 1rem!important;
  color: #374151!important;
  text-decoration: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.br-intro .list-group .list-group-item:hover {
  border-color: rgba(246,130,31,0.3)!important;
  box-shadow: 0 2px 8px rgba(246,130,31,0.08);
}
.br-intro .list-group .list-group-item h5 {
  font-size: 14px;
  font-weight: 600;
  color: #F6821F!important;
  margin: 0 0 0.4rem;
}
.br-intro .list-group .list-group-item p { margin: 0; color: #374151!important; font-size: 13px; line-height: 1.55; }

/* hint */
.hint { font-size: 13px; color: #6B7280; margin-top: 0.75rem; padding: 0.6rem 0.8rem; background: #FFF3E8; border-radius: 6px; border-left: 3px solid #F6821F; }

/* skyblue color overrides -> CF orange */
.br-intro ::v-deep [style*='color: skyblue'] { color: #F6821F!important; }

@media (max-width: 600px) {
  h1 { font-size: 1.6rem; }
  .table-row { flex-direction: column; }
  .col-feature, .col-free, .col-paid, .col-pages, .col-br { flex: none; width: 100%; border-right: none; border-bottom: 1px solid #E4E4E0; }
}
</style>